import { useState, useEffect } from "react";
import { Slider } from "@/components/ui/slider";
import api, { formatApiError, API } from "@/lib/api";
import StreakFlagModal from "@/components/app/StreakFlagModal";
import { SYMPTOM_OPTIONS, TRIBES } from "@/lib/tribes";
import { useOverride } from "@/context/ContentContext";
import { AlertCircle, Clock, Bell, Sparkles, Camera, Info } from "lucide-react";

// Baseline + midpoints + graduation — four evenly-spaced photo capture points that
// give a clear before / during / after comparison of tonsil / adenoid grading
// without asking for a photo every two weeks.
const TONSIL_PHOTO_SESSIONS = new Set([1, 4, 8, 12]);

const YNS = ["yes", "somewhat", "no"];
const YN = ["yes", "no"];
const UNSURE = "__unsure__";

function ObserveMore({ testid, onChange, active, tribe, clearValue = 0 }) {
  const activeStyle = { borderColor: tribe.accent, background: `${tribe.accent}18`, color: tribe.accent };
  // Toggle behavior: if already active, clicking it clears the "unsure" state so the
  // parent can move the slider / pick an option again. Otherwise, it enters "unsure".
  const handle = () => onChange(active ? clearValue : UNSURE);
  return (
    <button type="button" data-testid={`${testid}-unsure`} onClick={handle} style={active ? activeStyle : undefined} className={`inline-flex items-center gap-1 text-xs mt-1 px-3 py-1 rounded-full border transition ${active ? "font-semibold" : "border-edge text-ink-soft hover:border-current"}`}>
      <Clock size={11} /> {active ? "Tap again to undo" : "I need more time to observe this"}
    </button>
  );
}

function Pct({ label, value, onChange, testid, tribe }) {
  const isUnsure = value === UNSURE;
  return (
    <div className="mb-5">
      <div className="flex justify-between text-sm">
        <span className="text-ink-dark font-semibold">{label}</span>
        <span className="font-bold" style={{ color: tribe.accent }} data-testid={`${testid}-value`}>{isUnsure ? "—" : `${value}%`}</span>
      </div>
      {/* Keep the slider interactive even in "unsure" state — dragging it should
          replace the unsure marker with a real number so the parent can never get
          permanently stuck the way one customer did on 2026-09-16. */}
      <div className={isUnsure ? "opacity-60" : ""}>
        <Slider data-testid={testid} value={[isUnsure ? 0 : value]} onValueChange={(v) => onChange(v[0])} min={0} max={100} step={1} className="mt-2" />
      </div>
      <ObserveMore testid={testid} active={isUnsure} onChange={onChange} tribe={tribe} clearValue={0} />
    </div>
  );
}

function Choice({ label, value, options, onChange, testid, tribe }) {
  return (
    <div className="mb-4">
      <div className="text-sm text-ink-dark font-semibold mb-2">{label}</div>
      <div className="flex gap-2 flex-wrap">
        {options.map((o) => (
          <button key={o} type="button" data-testid={`${testid}-${o}`} onClick={() => onChange(o)}
            style={value === o ? { background: tribe.accent, color: tribe.panel_ink, borderColor: tribe.accent } : undefined}
            className={`px-4 py-2 rounded-full text-sm font-semibold border transition ${value === o ? "" : "bg-warm-white text-ink-mid border-edge hover:border-current"}`}>
            {o}
          </button>
        ))}
      </div>
      <ObserveMore testid={testid} active={value === UNSURE} onChange={onChange} tribe={tribe} />
    </div>
  );
}

function Section({ title, children, tribe }) {
  return (
    <div className="mb-8 pb-6 border-b border-edge last:border-0">
      <div className="text-xs uppercase tracking-widest font-bold mb-3" style={{ color: tribe.accent }}>{title}</div>
      {children}
    </div>
  );
}

export default function CheckInForm({ childId, sessionNumber, activeSymptoms = [], onDone, onDraftSaved, tribe }) {
  const t = tribe || TRIBES.neutral;
  const [breathing, setB] = useState({ aware_day: "no", nasal_day_pct: 0, aware_night: "no", nasal_night_pct: 0, easier: "no" });
  const [lips, setL] = useState({ aware_day: "no", aware_night: "no", strain: "strain", sealed_pct: 0 });
  const [tongue, setT] = useState({ aware_day: "no", tip_spot_pct: 0, mid_palate_pct: 0, back_palate_pct: 0, aware_night: "no", strain: "strain", tie_signs: "no", feels_normal: "no" });
  const [congestion, setC] = useState({ protocol: "no", blocking_nose: "no" });
  const [swallow, setS] = useState({ aware: "no", tip_glued: "no", contacts_teeth: "yes", chew_closed: "no", over_chew: "no", both_sides: "no", proper_easy: "no" });
  // "Other notes" replaces the pre-Feb-2026 "Top 5 symptoms" reflection field. Symptoms
  // now carry over automatically per-symptom with Better/Same/Worse tracking, so this
  // free-form box is for anything else the parent wants to mention this session.
  const [otherNotes, setOtherNotes] = useState("");
  const [struggles, setStr] = useState("");
  const [wins, setWins] = useState("");
  // Per-symptom current-check-in ratings and notes. `detail` here is the "New notes"
  // textarea for THIS check-in only — never pre-filled from prior sessions, only
  // preserved across draft saves of the same in-progress check-in.
  const [symptomUpdates, setSymptomUpdates] = useState({}); // {key: {rating, detail}}
  // Tonsil photo capture — only rendered at sessions 1, 4, 8, 12. All three fields
  // are optional; the section never blocks submit.
  const [tonsilPhotoPath, setTonsilPhotoPath] = useState(null);
  const [tonsilSeverity, setTonsilSeverity] = useState(null);
  const [tonsilNotes, setTonsilNotes] = useState("");
  const [tonsilUploading, setTonsilUploading] = useState(false);
  const [tonsilRefUrl] = [useOverride("checkin_reference", "tonsils", "image_url", "")];
  const showTonsilStep = TONSIL_PHOTO_SESSIONS.has(sessionNumber);
  // Read-only "Previous notes:" copy shown above the per-symptom New notes box.
  // Sourced from the MOST RECENT prior committed check-in's `symptom_updates[key].detail`.
  // Loaded once at mount; per-check-in history is kept intact server-side because we
  // never overwrite it — each check-in stores its own symptom_updates snapshot.
  const [previousSymptomNotes, setPreviousSymptomNotes] = useState({}); // {key: string}
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState("");
  const [showCongWarn, setShowCongWarn] = useState(false);
  const [feelClearer, setFeelClearer] = useState(null);
  const [reminderSet, setReminderSet] = useState(false);
  const [tongueTieStreak, setTongueTieStreak] = useState(0);
  const [showTongueTieFlag, setShowTongueTieFlag] = useState(false);

  // Load any saved draft; if no draft, pre-fill the six percentage sliders from the parent's
  // most recently submitted (non-draft) check-in so they adjust forward rather than restart at 0.
  // Also derive the "Previous notes:" map for each active symptom from that same last-committed
  // check-in — this is display-only and never seeds the New notes textarea.
  useEffect(() => {
    (async () => {
      let draft = null;
      try {
        const { data } = await api.get(`/checkins/${childId}/draft/${sessionNumber}`);
        draft = data;
      } catch (_) {}
      if (draft) {
        setB((prev) => ({ ...prev, ...(draft.breathing || {}) }));
        setL((prev) => ({ ...prev, ...(draft.lips || {}) }));
        setT((prev) => ({ ...prev, ...(draft.tongue || {}) }));
        setC((prev) => ({ ...prev, ...(draft.congestion || {}) }));
        setS((prev) => ({ ...prev, ...(draft.swallow || {}) }));
        setOtherNotes(draft.other_notes || "");
        setStr(draft.struggles || "");
        setWins(draft.wins || "");
        setSymptomUpdates(draft.symptom_updates || {});
        if (draft.tonsil_photo_path) setTonsilPhotoPath(draft.tonsil_photo_path);
        if (typeof draft.tonsil_severity === "number") setTonsilSeverity(draft.tonsil_severity);
        if (draft.tonsil_notes) setTonsilNotes(draft.tonsil_notes);
      }
      // Regardless of draft, fetch history to (a) fill slider defaults if there was no draft,
      // and (b) always build the "Previous notes" display map.
      try {
        const { data } = await api.get(`/checkins/${childId}`);
        const committed = (data || [])
          .filter((c) => !c.is_draft && c.session_number < sessionNumber)
          .sort((a, b) => (b.session_number || 0) - (a.session_number || 0));
        if (committed.length) {
          const last = committed[0];
          if (!draft) {
            setB((prev) => ({
              ...prev,
              nasal_day_pct: last.breathing?.nasal_day_pct ?? prev.nasal_day_pct,
              nasal_night_pct: last.breathing?.nasal_night_pct ?? prev.nasal_night_pct,
            }));
            setL((prev) => ({
              ...prev,
              sealed_pct: last.lips?.sealed_pct ?? prev.sealed_pct,
            }));
            setT((prev) => ({
              ...prev,
              tip_spot_pct: last.tongue?.tip_spot_pct ?? prev.tip_spot_pct,
              mid_palate_pct: last.tongue?.mid_palate_pct ?? prev.mid_palate_pct,
              back_palate_pct: last.tongue?.back_palate_pct ?? prev.back_palate_pct,
            }));
          }
          // Build previous-notes map: for each active symptom, use the note from the
          // MOST RECENT prior check-in that actually recorded one. Falls back through
          // older check-ins if the latest didn't leave a note for that symptom.
          const notesMap = {};
          for (const sKey of activeSymptoms) {
            for (const past of committed) {
              const detail = past.symptom_updates?.[sKey]?.detail;
              if (typeof detail === "string" && detail.trim()) { notesMap[sKey] = detail; break; }
            }
          }
          setPreviousSymptomNotes(notesMap);
        }
      } catch (_) {}
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [childId, sessionNumber]);

  const setBk = (k) => (v) => setB((x) => ({ ...x, [k]: v }));
  const setLk = (k) => (v) => setL((x) => ({ ...x, [k]: v }));
  const setTk = (k) => (v) => setT((x) => ({ ...x, [k]: v }));
  const setCk = (k) => (v) => { setC((x) => ({ ...x, [k]: v })); if (k === "blocking_nose" && v === "yes") setShowCongWarn(true); };
  const setSk = (k) => (v) => setS((x) => ({ ...x, [k]: v }));

  const anyUnsure = () => {
    const groups = [breathing, lips, tongue, congestion, swallow];
    return groups.some((g) => Object.values(g).some((v) => v === UNSURE));
  };

  const payload = () => ({
    child_id: childId, session_number: sessionNumber,
    breathing, lips, tongue, congestion, swallow,
    other_notes: otherNotes, struggles, wins,
    symptom_updates: symptomUpdates,
    tonsil_photo_path: showTonsilStep ? tonsilPhotoPath : null,
    tonsil_severity: showTonsilStep ? tonsilSeverity : null,
    tonsil_notes: showTonsilStep ? tonsilNotes : "",
  });

  const uploadTonsilPhoto = async (file) => {
    if (!file) return;
    const fd = new FormData(); fd.append("file", file);
    setTonsilUploading(true); setErr("");
    try {
      const { data } = await api.post("/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setTonsilPhotoPath(data.path);
    } catch (e) { setErr(formatApiError(e)); }
    finally { setTonsilUploading(false); }
  };

  const submit = async () => {
    setSaving(true); setErr("");
    try {
      const { data } = await api.post("/checkins", { ...payload(), is_draft: false });
      if (data && data.tongue_tie_flag_triggered && data.tongue_tie_streak > 0) {
        setTongueTieStreak(data.tongue_tie_streak);
        setShowTongueTieFlag(true);
      } else {
        onDone && onDone();
      }
    } catch (e) { setErr(formatApiError(e)); }
    finally { setSaving(false); }
  };

  const saveDraft = async () => {
    setSaving(true); setErr("");
    try {
      await api.post("/checkins", { ...payload(), is_draft: true });
      const remindAt = Date.now() + 24 * 60 * 60 * 1000;
      localStorage.setItem(`btt_reminder_${childId}_${sessionNumber}`, String(remindAt));
      setReminderSet(true);
      setTimeout(() => { onDraftSaved && onDraftSaved(); }, 1200);
    } catch (e) { setErr(formatApiError(e)); }
    finally { setSaving(false); }
  };

  return (
    <div>
      <Section title="Breathing" tribe={t}>
        <Choice label="Am I aware of how I am breathing during the day?" value={breathing.aware_day} options={YNS} onChange={setBk("aware_day")} testid="chk-b-aware-day" tribe={t} />
        <Pct label="% of the time nasal breathing during the day" value={breathing.nasal_day_pct} onChange={setBk("nasal_day_pct")} testid="chk-b-nasal-day" tribe={t} />
        <Choice label="Am I aware of how I am breathing at night?" value={breathing.aware_night} options={YNS} onChange={setBk("aware_night")} testid="chk-b-aware-night" tribe={t} />
        <Pct label="% of the time nasal breathing at night" value={breathing.nasal_night_pct} onChange={setBk("nasal_night_pct")} testid="chk-b-nasal-night" tribe={t} />
        <Choice label="Is nasal breathing becoming easier?" value={breathing.easier} options={YNS} onChange={setBk("easier")} testid="chk-b-easier" tribe={t} />
      </Section>
      <Section title="Lips" tribe={t}>
        <Choice label="Aware of what my lips are doing during the day?" value={lips.aware_day} options={YNS} onChange={setLk("aware_day")} testid="chk-l-aware-day" tribe={t} />
        <Choice label="Aware of what my lips are doing at night?" value={lips.aware_night} options={YNS} onChange={setLk("aware_night")} testid="chk-l-aware-night" tribe={t} />
        <Choice label="Lip seal feels…" value={lips.strain} options={["easy", "some strain", "strain"]} onChange={setLk("strain")} testid="chk-l-strain" tribe={t} />
        <Pct label="% of the time lips are sealed" value={lips.sealed_pct} onChange={setLk("sealed_pct")} testid="chk-l-sealed" tribe={t} />
      </Section>
      <Section title="Tongue" tribe={t}>
        <Choice label="Aware of where my tongue rests during the day?" value={tongue.aware_day} options={YNS} onChange={setTk("aware_day")} testid="chk-t-aware-day" tribe={t} />
        <Pct label="% of the time tip is on the Spot" value={tongue.tip_spot_pct} onChange={setTk("tip_spot_pct")} testid="chk-t-tip" tribe={t} />
        <Pct label="% of the time middle of tongue is on the palate" value={tongue.mid_palate_pct} onChange={setTk("mid_palate_pct")} testid="chk-t-mid" tribe={t} />
        <Pct label="% of the time back of tongue is on the palate" value={tongue.back_palate_pct} onChange={setTk("back_palate_pct")} testid="chk-t-back" tribe={t} />
        <Choice label="Aware of what my tongue is doing at night?" value={tongue.aware_night} options={YNS} onChange={setTk("aware_night")} testid="chk-t-aware-night" tribe={t} />
        <Choice label="Tongue resting on the palate feels…" value={tongue.strain} options={["easy", "some strain", "strain"]} onChange={setTk("strain")} testid="chk-t-strain" tribe={t} />
        <Choice label="Do I feel or see signs of tongue tie?" value={tongue.tie_signs} options={YN} onChange={setTk("tie_signs")} testid="chk-t-tie" tribe={t} />
        <Choice label="Is it starting to feel normal for tongue to rest on the palate?" value={tongue.feels_normal} options={YNS} onChange={setTk("feels_normal")} testid="chk-t-normal" tribe={t} />
      </Section>
      <Section title="Congestion" tribe={t}>
        <Choice label="Have I been doing a protocol to keep my nose clear?" value={congestion.protocol} options={YN} onChange={setCk("protocol")} testid="chk-c-protocol" tribe={t} />
        <Choice label="Is congestion stopping me from breathing through my nose?" value={congestion.blocking_nose} options={YN} onChange={setCk("blocking_nose")} testid="chk-c-blocking" tribe={t} />
        {showCongWarn && (
          <div className="mt-4 p-4 rounded-2xl border" style={{ background: t.panel_soft, borderColor: t.accent }}>
            <div className="flex items-start gap-3">
              <AlertCircle size={20} className="mt-0.5" style={{ color: t.accent }} />
              <div className="flex-1">
                <div className="font-semibold text-navy">We noticed you marked congestion.</div>
                <div className="text-sm text-ink-mid mt-1">Let's clear the airway first — nasal breathing only works if the passages are open.</div>
                <ul className="mt-3 space-y-1.5 text-sm text-ink-dark list-disc pl-5">
                  <li>Blow the nose morning and night, and after showers or swimming.</li>
                  <li>Use a plain, all-natural nasal saline spray (no steroids or medication).</li>
                  <li>If that's not enough, try a full nasal and sinus rinse to flush more thoroughly.</li>
                  <li>At night, try Breathe Right strips (external) or nasal mutes (internal).</li>
                  <li>Aim for 30-50% humidity in the bedroom (an air purifier can help too).</li>
                  <li>Give any new tool two consistent weeks before deciding if it's working — introduce one thing at a time.</li>
                </ul>
                <a href="/learn?open=nasal_sleep_hygiene" target="_blank" rel="noopener noreferrer" data-testid="cong-learn-module" className="mt-3 inline-flex items-center gap-2 text-sm underline font-semibold" style={{ color: t.accent }}>
                  Open the full Nasal &amp; Sleep Hygiene module
                </a>
                <div className="mt-3 text-sm text-ink-dark font-semibold">Are you feeling clearer?</div>
                <div className="flex gap-2 mt-2">
                  <button type="button" data-testid="cong-clearer-yes" onClick={() => setFeelClearer(true)} style={feelClearer === true ? { background: t.accent, color: t.panel_ink, borderColor: t.accent } : undefined} className={`px-4 py-2 rounded-full text-sm border ${feelClearer === true ? "" : "border-edge"}`}>Yes</button>
                  <button type="button" data-testid="cong-clearer-no" onClick={() => setFeelClearer(false)} style={feelClearer === false ? { background: t.accent, color: t.panel_ink, borderColor: t.accent } : undefined} className={`px-4 py-2 rounded-full text-sm border ${feelClearer === false ? "" : "border-edge"}`}>No</button>
                </div>
                {feelClearer === false && (
                  <div className="mt-3 text-sm text-ink-mid bg-warm-white border border-edge rounded-xl p-3">
                    Try saline rinse, warm steam, and elevating the head at sleep. If congestion persists beyond a week, consult your doctor.
                  </div>
                )}
              </div>
            </div>
          </div>
        )}
      </Section>
      <Section title="Swallow" tribe={t}>
        <Choice label="Aware of how I swallow?" value={swallow.aware} options={YNS} onChange={setSk("aware")} testid="chk-s-aware" tribe={t} />
        <Choice label="Does the tip of my tongue stay glued to the Spot when swallowing?" value={swallow.tip_glued} options={YNS} onChange={setSk("tip_glued")} testid="chk-s-glued" tribe={t} />
        <Choice label="Does my tongue contact my front or back teeth when swallowing?" value={swallow.contacts_teeth} options={["front", "back", "no"]} onChange={setSk("contacts_teeth")} testid="chk-s-contacts" tribe={t} />
        <Choice label="Am I chewing with my lips together?" value={swallow.chew_closed} options={YNS} onChange={setSk("chew_closed")} testid="chk-s-closed" tribe={t} />
        <Choice label="Am I over-chewing my food?" value={swallow.over_chew} options={YN} onChange={setSk("over_chew")} testid="chk-s-over" tribe={t} />
        <Choice label="Am I chewing on both sides?" value={swallow.both_sides} options={YNS} onChange={setSk("both_sides")} testid="chk-s-both" tribe={t} />
        <Choice label="Does the proper swallow pattern feel easy?" value={swallow.proper_easy} options={YNS} onChange={setSk("proper_easy")} testid="chk-s-easy" tribe={t} />
      </Section>
      {activeSymptoms.length > 0 && (
        <Section title="Symptoms" tribe={t}>
          <p className="text-sm text-ink-mid mb-4">
            How is each symptom right now, compared to your baseline or last check-in?
          </p>
          {activeSymptoms.map((sKey) => {
            const opt = SYMPTOM_OPTIONS.find((s) => s.key === sKey);
            const name = opt ? opt.name : sKey;
            const rec = symptomUpdates[sKey] || {};
            const isResolved = rec.rating === "resolved";
            const prev = previousSymptomNotes[sKey];
            const setSymRating = (rating) => setSymptomUpdates((u) => ({ ...u, [sKey]: { ...(u[sKey] || {}), rating } }));
            const setSymDetail = (detail) => setSymptomUpdates((u) => ({ ...u, [sKey]: { ...(u[sKey] || {}), detail } }));
            return (
              <div key={sKey} className="mb-5 pb-5 border-b border-edge/70 last:border-0 last:pb-0" data-testid={`chk-sym-${sKey}`}>
                <div className="text-sm text-ink-dark font-semibold mb-2">{name}</div>
                <div className="flex gap-2 flex-wrap">
                  {["better", "same", "worse"].map((r) => (
                    <button
                      key={r}
                      type="button"
                      data-testid={`chk-sym-${sKey}-${r}`}
                      onClick={() => setSymRating(r)}
                      style={rec.rating === r ? { background: t.accent, color: t.panel_ink, borderColor: t.accent } : undefined}
                      className={`px-4 py-2 rounded-full text-sm font-semibold border transition capitalize ${rec.rating === r ? "" : "bg-warm-white text-ink-mid border-edge hover:border-current"}`}
                    >
                      {r}
                    </button>
                  ))}
                  <button
                    type="button"
                    data-testid={`chk-sym-${sKey}-resolved`}
                    onClick={() => setSymRating("resolved")}
                    style={isResolved ? { background: t.spark, color: t.spark_ink, borderColor: t.spark } : undefined}
                    className={`px-4 py-2 rounded-full text-sm font-semibold border transition ${isResolved ? "" : "bg-warm-white text-ink-mid border-edge hover:border-current"}`}
                  >
                    This isn't a concern anymore
                  </button>
                </div>
                {isResolved && (
                  <div className="mt-3 p-3 rounded-xl text-sm text-ink-dark flex items-start gap-2 border" style={{ background: `${t.spark}18`, borderColor: t.spark }} data-testid={`chk-sym-${sKey}-celebrate`}>
                    <Sparkles size={14} className="mt-0.5" style={{ color: t.spark }} />
                    <span>That's wonderful! We'll stop asking about this one — your progress is real.</span>
                  </div>
                )}
                {/* Previous notes: read-only display of the last non-empty note recorded
                    for this symptom. Omitted entirely when there's nothing to show. */}
                {prev && (
                  <div
                    className="mt-3 rounded-xl border px-3 py-2 text-xs"
                    style={{ background: t.panel_soft, borderColor: `${t.accent}55`, color: t.ink }}
                    data-testid={`chk-sym-${sKey}-previous`}
                  >
                    <div className="text-[10px] uppercase tracking-[0.2em] font-bold mb-1" style={{ color: t.accent }}>
                      Previous notes
                    </div>
                    <div className="whitespace-pre-line leading-relaxed">{prev}</div>
                  </div>
                )}
                {/* New notes: always available regardless of Better/Same/Worse rating.
                    Loads blank on every check-in; the entered value is stored as its own
                    entry on this session's snapshot without touching prior sessions. */}
                <div className="mt-3">
                  <label
                    className="text-[10px] uppercase tracking-[0.2em] font-bold mb-1 block"
                    style={{ color: t.accent }}
                    htmlFor={`chk-sym-${sKey}-detail`}
                  >
                    New notes
                  </label>
                  <textarea
                    id={`chk-sym-${sKey}-detail`}
                    data-testid={`chk-sym-${sKey}-detail`}
                    value={rec.detail || ""}
                    onChange={(e) => setSymDetail(e.target.value)}
                    rows={2}
                    placeholder="Optional — anything to add about this symptom right now?"
                    className="w-full px-4 py-2.5 rounded-xl border border-edge bg-cream focus:outline-none focus:ring-2 text-ink-dark text-sm"
                    style={{ "--tw-ring-color": t.accent }}
                  />
                </div>
              </div>
            );
          })}
        </Section>
      )}

      {showTonsilStep && (
        <Section title={sessionNumber === 1 ? "Tonsil check · Baseline" : `Tonsil check · Session ${sessionNumber}`} tribe={t}>
          <p className="text-sm text-ink-mid mb-4">
            Optional but powerful — a snapshot now, at sessions 4 and 8, and again at session 12 gives you a real before/during/after view of your child's tonsil space.
          </p>

          {tonsilRefUrl ? (
            <div className="mb-4">
              <div className="text-[10px] uppercase tracking-[0.2em] font-bold mb-2" style={{ color: t.accent }}>Reference — match this framing</div>
              <div className="rounded-2xl overflow-hidden border border-edge bg-cream">
                <img src={tonsilRefUrl} alt="Tonsil photo reference" className="w-full max-h-72 object-contain" data-testid="chk-tonsil-reference" />
              </div>
            </div>
          ) : (
            <div className="mb-4 p-3 rounded-xl border border-dashed border-edge text-xs text-ink-soft flex items-start gap-2" data-testid="chk-tonsil-reference-missing">
              <Info size={14} className="mt-0.5" />
              <span>Reference photo not set yet. An admin can add one under CMS → Check-in reference images → Tonsil photo reference.</span>
            </div>
          )}

          <div className="mb-4 rounded-xl bg-cream border border-edge px-4 py-3 text-sm text-ink-dark">
            <div className="font-semibold text-navy mb-1.5">How to take the photo</div>
            <ul className="list-disc pl-5 space-y-1 text-ink-mid text-xs leading-relaxed">
              <li>Have your child open wide and say "ah"</li>
              <li>Use a tongue depressor or the back of a clean spoon if you can't see clearly</li>
              <li>Take the photo in the same position every time — always sitting up, or always lying down (don't mix between sessions)</li>
              <li>Aim for good lighting; use your phone flashlight if needed</li>
            </ul>
          </div>

          <div className="mb-4">
            <div className="text-xs uppercase tracking-widest text-ink-soft font-semibold mb-2">Tonsil grade (0–4)</div>
            <div className="flex gap-2 flex-wrap">
              {[0, 1, 2, 3, 4].map((g) => (
                <button
                  key={g}
                  type="button"
                  data-testid={`chk-tonsil-grade-${g}`}
                  onClick={() => setTonsilSeverity(tonsilSeverity === g ? null : g)}
                  style={tonsilSeverity === g ? { background: t.accent, color: t.panel_ink, borderColor: t.accent } : undefined}
                  className={`w-11 h-11 rounded-full font-bold text-sm border transition ${tonsilSeverity === g ? "" : "bg-warm-white text-ink-mid border-edge hover:border-current"}`}
                >
                  {g}
                </button>
              ))}
            </div>
            <div className="text-xs text-ink-soft mt-1.5">0 = clear · 1 = &lt;25% · 2 = 25–50% · 3 = 50–75% · 4 = touching / kissing tonsils</div>
          </div>

          <div className="mb-2">
            <div className="text-xs uppercase tracking-widest text-ink-soft font-semibold mb-2">Photo (optional)</div>
            {tonsilPhotoPath && (
              <div className="mb-3 rounded-xl overflow-hidden border border-edge bg-cream inline-block">
                <img src={`${API}/files/${tonsilPhotoPath}?auth=${localStorage.getItem("btt_token")}`} alt="Your tonsil photo" className="max-h-48" data-testid="chk-tonsil-preview" />
              </div>
            )}
            <div>
              <label className="inline-flex items-center gap-2 text-sm cursor-pointer font-semibold px-4 py-2 rounded-full border-2 hover:border-current transition" style={{ borderColor: `${t.accent}55`, color: t.accent }}>
                <Camera size={14} />
                <span>{tonsilUploading ? "Uploading…" : tonsilPhotoPath ? "Replace photo" : "Upload / take photo"}</span>
                <input type="file" accept="image/*" capture="environment" data-testid="chk-tonsil-photo" className="hidden" onChange={(e) => e.target.files[0] && uploadTonsilPhoto(e.target.files[0])} disabled={tonsilUploading} />
              </label>
              {tonsilPhotoPath && (
                <button type="button" data-testid="chk-tonsil-remove" onClick={() => setTonsilPhotoPath(null)} className="ml-2 text-xs text-ink-soft hover:text-mauve underline">
                  Remove
                </button>
              )}
            </div>
            <p className="mt-2 text-xs text-ink-soft">Saved privately to your family. Also appears on the Structural Roadblocks screen with your photo history.</p>
          </div>

          <div className="mt-4">
            <label className="text-xs uppercase tracking-widest text-ink-soft font-semibold" htmlFor="chk-tonsil-notes">Notes (optional)</label>
            <textarea
              id="chk-tonsil-notes"
              data-testid="chk-tonsil-notes"
              value={tonsilNotes}
              onChange={(e) => setTonsilNotes(e.target.value)}
              rows={2}
              placeholder="Anything you noticed while taking the photo?"
              className="mt-1 w-full px-4 py-2.5 rounded-xl border border-edge bg-cream focus:outline-none text-ink-dark text-sm"
            />
          </div>
        </Section>
      )}

      <Section title="Reflection" tribe={t}>
        <div className="mb-3">
          <label className="text-sm text-ink-dark font-semibold">Other notes</label>
          <p className="text-xs text-ink-soft mt-0.5">Anything else you'd like to mention this session — not tied to a specific symptom.</p>
          <textarea data-testid="chk-reflect-other-notes" value={otherNotes} onChange={(e) => setOtherNotes(e.target.value)} rows={3} className="mt-2 w-full px-4 py-3 rounded-xl border border-edge bg-cream focus:outline-none text-ink-dark" />
        </div>
        <div className="mb-3">
          <label className="text-sm text-ink-dark font-semibold">What did I struggle with?</label>
          <textarea data-testid="chk-reflect-struggles" value={struggles} onChange={(e) => setStr(e.target.value)} rows={2} className="mt-1 w-full px-4 py-3 rounded-xl border border-edge bg-cream focus:outline-none text-ink-dark" />
        </div>
        <div className="mb-3">
          <label className="text-sm text-ink-dark font-semibold">Wins</label>
          <textarea data-testid="chk-reflect-wins" value={wins} onChange={(e) => setWins(e.target.value)} rows={2} className="mt-1 w-full px-4 py-3 rounded-xl border border-edge bg-cream focus:outline-none text-ink-dark" />
        </div>
      </Section>

      {reminderSet && (
        <div className="mb-4 p-4 rounded-2xl text-sm text-ink-dark flex items-center gap-2 border" style={{ background: `${t.spark}15`, borderColor: t.spark }} data-testid="reminder-set">
          <Bell size={16} style={{ color: t.spark }} /> Draft saved. We'll remind you to finish in 24 hours.
        </div>
      )}

      {anyUnsure() && (
        <div className="mb-3 p-3 rounded-xl text-sm text-ink-dark flex items-center gap-2 border" style={{ background: t.panel_soft, borderColor: t.accent }}>
          <Clock size={14} style={{ color: t.accent }} /> Some answers are marked "need more time" — save this as a draft and come back tomorrow.
        </div>
      )}

      {err && <div className="text-sm text-red-700 bg-red-50 border border-red-200 rounded-lg px-3 py-2 mb-3">{err}</div>}

      <div className="flex gap-3 flex-wrap">
        <button data-testid="chk-submit" onClick={submit} disabled={saving || anyUnsure()} style={{ background: t.accent, color: t.panel_ink }} className="flex-1 px-6 py-3.5 rounded-full font-semibold hover:opacity-90 disabled:opacity-50 transition-opacity">
          {saving ? "Saving…" : "Save check-in"}
        </button>
        <button data-testid="chk-save-draft" onClick={saveDraft} disabled={saving} style={{ borderColor: `${t.accent}55`, color: t.accent }} className="px-6 py-3.5 rounded-full font-semibold border-2 hover:border-current disabled:opacity-50 inline-flex items-center gap-2">
          <Clock size={14} /> Save draft · remind me in 24h
        </button>
      </div>

      {showTongueTieFlag && (
        <StreakFlagModal
          streak={tongueTieStreak}
          signalLabel="tongue-tie signs"
          learnHref="/learn?open=roadblocks"
          onClose={() => { setShowTongueTieFlag(false); onDone && onDone(); }}
        />
      )}
    </div>
  );
}
